import { format } from "date-fns";
import type {
  Conclusion,
  Job,
  Workflow,
  WorkflowRun,
  WorkflowRunUsage,
} from "@/lib/github/types";
import type {
  ActivityCell,
  AttemptsMode,
  BranchGroup,
  BranchStats,
  BucketSize,
  FailureBreakdown,
  FailureSlice,
  Overview,
  PeriodDuration,
  PeriodRunsRow,
  PeriodSuccessRate,
  RunnerOSBreakdown,
  SlowestJob,
  TriggerDistribution,
  WorkflowStats,
} from "@/lib/analytics/types";
import { bucketByDayHour, bucketByPeriod, bucketFmt, type DayOfWeek } from "@/lib/analytics/buckets";
import { p50, p95, percentiles } from "@/lib/analytics/percentiles";
import { computeQueueTimeMs, computeQueueTimeTrend } from "@/lib/analytics/queue-time";
import { computeRerunRate, firstPassOnly } from "@/lib/analytics/attempts";

export { computeQueueTimeTrend };

function runDurationMs(run: WorkflowRun): number {
  const start = run.run_started_at ?? run.created_at;
  const end = run.updated_at;
  if (!start || !end) return 0;
  const startMs = Date.parse(start);
  const endMs = Date.parse(end);
  if (Number.isNaN(startMs) || Number.isNaN(endMs)) return 0;
  return Math.max(0, endMs - startMs);
}

function runDate(run: WorkflowRun): Date | null {
  const raw = run.run_started_at ?? run.created_at;
  if (!raw) return null;
  const ms = Date.parse(raw);
  if (Number.isNaN(ms)) return null;
  return new Date(ms);
}

function successRateOf(runs: WorkflowRun[]): number {
  const finished = runs.filter((r) => r.conclusion != null);
  if (finished.length === 0) return 0;
  const ok = finished.filter((r) => r.conclusion === "success").length;
  return ok / finished.length;
}

function applyMode(runs: WorkflowRun[], mode: AttemptsMode): WorkflowRun[] {
  return mode === "first-pass" ? firstPassOnly(runs) : runs;
}

export function computeOverview(
  runs: WorkflowRun[],
  mode: AttemptsMode = "first-pass",
): Overview {
  const filtered = applyMode(runs, mode);

  const durations = filtered.map((r) => runDurationMs(r)).filter((d) => d > 0);
  const activeWorkflowIds = new Set<number>();
  for (const r of filtered) {
    if (typeof r.workflow_id === "number") activeWorkflowIds.add(r.workflow_id);
  }
  const totalMs = durations.reduce((acc, d) => acc + d, 0);
  const estimatedBillableMinutes = Math.round(totalMs / 60000);

  // Queue time — computed on filtered runs, excluding null queue times from aggregates
  const queueTimes: number[] = [];
  for (const r of filtered) {
    const qt = computeQueueTimeMs(r);
    if (qt !== null) queueTimes.push(qt);
  }

  // Re-run rate is always computed on the full dataset (mode-independent)
  const rerunRate = computeRerunRate(runs);

  // firstPassSuccessRate: reuse `filtered` when mode is already "first-pass"
  // to avoid a redundant full-pass filter.
  const firstPass = mode === "first-pass" ? filtered : firstPassOnly(runs);
  const firstPassRate = successRateOf(firstPass);

  // Single sort per array for paired percentiles (p50 + p95).
  const [p50Dur, p95Dur] = percentiles(durations, [0.5, 0.95]);
  const [p50Queue, p95Queue] = percentiles(queueTimes, [0.5, 0.95]);

  return {
    totalRuns: filtered.length,
    successRate: successRateOf(filtered),
    firstPassSuccessRate: firstPassRate,
    p50DurationMs: p50Dur ?? 0,
    p95DurationMs: p95Dur ?? 0,
    estimatedBillableMinutes,
    activeWorkflows: activeWorkflowIds.size,
    p50QueueTimeMs: p50Queue ?? 0,
    p95QueueTimeMs: p95Queue ?? 0,
    rerunRate,
  };
}

export function computeRunsOverTime(
  runs: WorkflowRun[],
  workflows: Workflow[],
  mode: AttemptsMode = "first-pass",
  bucket: BucketSize = "month",
): PeriodRunsRow[] {
  const filtered = applyMode(runs, mode);
  const workflowIds = new Set(workflows.map((w) => w.id));
  const byPeriod = bucketByPeriod(filtered, bucket, (r) => runDate(r));
  const rows: PeriodRunsRow[] = [];
  for (const [period, group] of byPeriod) {
    const perWorkflow: Record<number, number> = {};
    for (const r of group) {
      const wfId = r.workflow_id;
      if (typeof wfId !== "number") continue;
      if (!workflowIds.has(wfId)) continue;
      perWorkflow[wfId] = (perWorkflow[wfId] ?? 0) + 1;
    }
    rows.push({ period, perWorkflow });
  }
  return rows;
}

export function computeSuccessRateTrend(
  runs: WorkflowRun[],
  mode: AttemptsMode = "first-pass",
  bucket: BucketSize = "month",
): PeriodSuccessRate[] {
  const filtered = applyMode(runs, mode);
  const byPeriod = bucketByPeriod(filtered, bucket, (r) => runDate(r));
  const rows: PeriodSuccessRate[] = [];
  for (const [period, group] of byPeriod) {
    const finished = group.filter((r) => r.conclusion != null);
    const success = finished.filter((r) => r.conclusion === "success").length;
    rows.push({
      period,
      total: finished.length,
      success,
      rate: finished.length === 0 ? 0 : success / finished.length,
    });
  }
  return rows;
}

export function computeDurationTrend(
  runs: WorkflowRun[],
  mode: AttemptsMode = "first-pass",
  bucket: BucketSize = "month",
): PeriodDuration[] {
  const filtered = applyMode(runs, mode);
  const byPeriod = bucketByPeriod(filtered, bucket, (r) => runDate(r));
  const rows: PeriodDuration[] = [];
  for (const [period, group] of byPeriod) {
    const durations = group.map((r) => runDurationMs(r)).filter((d) => d > 0);
    rows.push({
      period,
      p50Ms: p50(durations),
      p95Ms: p95(durations),
    });
  }
  return rows;
}

export function computeWorkflowStats(
  runs: WorkflowRun[],
  workflows: Workflow[],
  mode: AttemptsMode = "first-pass",
  bucket: BucketSize = "month",
): WorkflowStats[] {
  const filtered = applyMode(runs, mode);
  const fmt = bucketFmt(bucket);

  // Single pass over filtered: compute period key per run and group by workflow.
  // Avoids re-formatting each run's date W+1 times (once for bucketByPeriod + once
  // per workflow inner loop). Period keys are deduplicated into the periods array.
  interface EnrichedRun { run: WorkflowRun; periodKey: string | null }
  const runsByWorkflow = new Map<number, EnrichedRun[]>();
  const periodOrder: string[] = [];
  const periodIndex = new Map<string, number>();

  for (const r of filtered) {
    if (typeof r.workflow_id !== "number") continue;
    const d = runDate(r);
    const periodKey = d ? format(d, fmt) : null;
    if (periodKey !== null && !periodIndex.has(periodKey)) {
      periodIndex.set(periodKey, periodOrder.length);
      periodOrder.push(periodKey);
    }
    const enriched: EnrichedRun = { run: r, periodKey };
    const list = runsByWorkflow.get(r.workflow_id);
    if (list) {
      list.push(enriched);
    } else {
      runsByWorkflow.set(r.workflow_id, [enriched]);
    }
  }

  // Sort periodOrder ascending so periodTrend arrays stay chronological.
  periodOrder.sort((a, b) => a.localeCompare(b));
  // Rebuild index after sort. Iterate with entries() so `key` is typed as
  // string from the tuple — no noUncheckedIndexedAccess cast required.
  periodIndex.clear();
  for (const [i, key] of periodOrder.entries()) {
    periodIndex.set(key, i);
  }

  // Also group all (unfiltered) runs for re-run rate computation per-workflow.
  const allRunsByWorkflow = new Map<number, WorkflowRun[]>();
  for (const r of runs) {
    if (typeof r.workflow_id !== "number") continue;
    const list = allRunsByWorkflow.get(r.workflow_id);
    if (list) {
      list.push(r);
    } else {
      allRunsByWorkflow.set(r.workflow_id, [r]);
    }
  }

  return workflows
    .map((wf) => {
      const wfRuns = runsByWorkflow.get(wf.id) ?? [];
      const allWfRuns = allRunsByWorkflow.get(wf.id) ?? [];
      const durations: number[] = [];
      const periodTrend = new Array<number>(periodOrder.length).fill(0);
      const queueTimes: number[] = [];

      for (const { run: r, periodKey } of wfRuns) {
        const dur = runDurationMs(r);
        if (dur > 0) durations.push(dur);
        if (periodKey !== null) {
          const idx = periodIndex.get(periodKey);
          if (idx !== undefined) periodTrend[idx] = (periodTrend[idx] ?? 0) + 1;
        }
        const qt = computeQueueTimeMs(r);
        if (qt !== null) queueTimes.push(qt);
      }

      const totalMs = durations.reduce((a, b) => a + b, 0);
      const avg = durations.length ? totalMs / durations.length : 0;
      const avgQueueTimeMs = queueTimes.length
        ? queueTimes.reduce((a, b) => a + b, 0) / queueTimes.length
        : 0;

      return {
        workflowId: wf.id,
        name: wf.name ?? wf.path ?? `Workflow #${wf.id}`,
        path: wf.path ?? "",
        runs: wfRuns.length,
        successRate: successRateOf(wfRuns.map((e) => e.run)),
        avgDurationMs: avg,
        p95DurationMs: p95(durations),
        estimatedMinutes: Math.round(totalMs / 60000),
        periodTrend,
        avgQueueTimeMs,
        rerunRate: computeRerunRate(allWfRuns),
      } satisfies WorkflowStats;
    })
    .sort((a, b) => b.runs - a.runs);
}

const KNOWN_CONCLUSIONS: ReadonlySet<Conclusion> = new Set<Conclusion>([
  "success",
  "failure",
  "cancelled",
  "timed_out",
  "startup_failure",
  "action_required",
  "neutral",
  "skipped",
]);

/** Type guard — narrows an unknown API string to our Conclusion union. */
function isConclusion(value: unknown): value is Conclusion {
  return typeof value === "string" && KNOWN_CONCLUSIONS.has(value as Conclusion);
}

export function computeFailureBreakdown(
  runs: WorkflowRun[],
  workflows: Workflow[],
  mode: AttemptsMode = "first-pass",
): FailureBreakdown {
  const filtered = applyMode(runs, mode);
  const byConclusion = new Map<Conclusion, number>();
  for (const r of filtered) {
    if (!isConclusion(r.conclusion)) continue;
    byConclusion.set(r.conclusion, (byConclusion.get(r.conclusion) ?? 0) + 1);
  }
  const slices: FailureSlice[] = [...byConclusion.entries()]
    .map(([conclusion, count]) => ({ conclusion, count }))
    .sort((a, b) => b.count - a.count);

  const wfById = new Map(workflows.map((w) => [w.id, w]));
  const grouped = new Map<number, { total: number; failures: number }>();
  for (const r of filtered) {
    if (typeof r.workflow_id !== "number") continue;
    const cur = grouped.get(r.workflow_id) ?? { total: 0, failures: 0 };
    cur.total += 1;
    if (
      r.conclusion === "failure" ||
      r.conclusion === "timed_out" ||
      r.conclusion === "startup_failure"
    ) {
      cur.failures += 1;
    }
    grouped.set(r.workflow_id, cur);
  }

  const topFailingWorkflows = [...grouped.entries()]
    .filter(([, v]) => v.failures > 0)
    .map(([workflowId, v]) => {
      const wf = wfById.get(workflowId);
      return {
        workflowId,
        name: wf?.name ?? wf?.path ?? `Workflow #${workflowId}`,
        failures: v.failures,
        total: v.total,
        failureRate: v.total === 0 ? 0 : v.failures / v.total,
      };
    })
    .sort((a, b) => b.failures - a.failures)
    .slice(0, 5);

  return { slices, topFailingWorkflows };
}

export function computeActivityHeatmap(
  runs: WorkflowRun[],
  useUTC = false,
  mode: AttemptsMode = "first-pass",
): ActivityCell[] {
  const filtered = applyMode(runs, mode);
  const buckets = bucketByDayHour(filtered, (r) => runDate(r), useUTC);
  const cells: ActivityCell[] = [];
  // Iterate the Monday-first DayOfWeek tuple so each index is typed as the
  // narrow literal union rather than a plain `number` needing a cast.
  const daysOfWeek: readonly DayOfWeek[] = [0, 1, 2, 3, 4, 5, 6];
  for (const day of daysOfWeek) {
    for (let hour = 0; hour < 24; hour++) {
      const mapKey = `${day}-${hour}`;
      const bucket = buckets.get(mapKey);
      cells.push({
        dayOfWeek: day,
        hour,
        count: bucket?.items.length ?? 0,
      });
    }
  }
  return cells;
}

export function computeRunnerOSBreakdown(
  usages: WorkflowRunUsage[],
): RunnerOSBreakdown {
  let ubuntuMs = 0;
  let windowsMs = 0;
  let macosMs = 0;
  for (const usage of usages) {
    const billable = usage.billable ?? {};
    ubuntuMs += billable.UBUNTU?.total_ms ?? 0;
    windowsMs += billable.WINDOWS?.total_ms ?? 0;
    macosMs += billable.MACOS?.total_ms ?? 0;
  }
  const ubuntu = Math.round(ubuntuMs / 60000);
  const windows = Math.round(windowsMs / 60000);
  const macos = Math.round(macosMs / 60000);
  return {
    ubuntu,
    windows,
    macos,
    billableWeightedMinutes: ubuntu * 1 + windows * 2 + macos * 10,
  };
}

export function computeTriggerEvents(
  runs: WorkflowRun[],
  mode: AttemptsMode = "first-pass",
): TriggerDistribution[] {
  const filtered = applyMode(runs, mode);
  const counts = new Map<string, number>();
  for (const r of filtered) {
    const event = r.event ?? "unknown";
    counts.set(event, (counts.get(event) ?? 0) + 1);
  }
  return [...counts.entries()]
    .map(([event, count]) => ({ event, count }))
    .sort((a, b) => b.count - a.count);
}

export function computeSlowestJobs(
  jobs: Array<{ job: Job; run: WorkflowRun; workflowName: string | null }>,
  top = 10,
): SlowestJob[] {
  return jobs
    .map(({ job, run, workflowName }) => {
      const start = job.started_at ? Date.parse(job.started_at) : NaN;
      const end = job.completed_at ? Date.parse(job.completed_at) : NaN;
      const durationMs =
        Number.isFinite(start) && Number.isFinite(end)
          ? Math.max(0, end - start)
          : 0;
      const conclusion = isConclusion(job.conclusion) ? job.conclusion : null;
      return {
        jobId: job.id,
        name: job.name,
        runId: run.id,
        workflowName,
        durationMs,
        conclusion,
        htmlUrl: job.html_url ?? null,
      } satisfies SlowestJob;
    })
    .sort((a, b) => b.durationMs - a.durationMs)
    .slice(0, top);
}

function classifyBranch(run: WorkflowRun): BranchGroup {
  const event = run.event ?? "";
  if (
    event === "pull_request" ||
    event === "pull_request_target" ||
    event === "pull_request_review"
  ) {
    return "pr";
  }
  const branch = run.head_branch;
  if (branch === "main" || branch === "master") {
    return "default";
  }
  return "other";
}

interface BranchAccumulator {
  runs: number;
  finishedRuns: number;
  successRuns: number;
  totalDurationMs: number;
  durationCount: number;
  group: BranchGroup;
}

export function computeBranchStats(
  runs: WorkflowRun[],
  mode: AttemptsMode,
  limit = 10,
): BranchStats[] {
  const filtered = applyMode(runs, mode);

  // Single-pass group by branch name — O(N).
  const byBranch = new Map<string, BranchAccumulator>();

  for (const r of filtered) {
    const branchName = r.head_branch ?? "(detached)";
    const group = classifyBranch(r);
    const dur = runDurationMs(r);
    const isFinished = r.conclusion != null;
    const isSuccess = r.conclusion === "success";

    const acc = byBranch.get(branchName);
    if (acc) {
      acc.runs += 1;
      if (isFinished) {
        acc.finishedRuns += 1;
        if (isSuccess) acc.successRuns += 1;
      }
      if (dur > 0) {
        acc.totalDurationMs += dur;
        acc.durationCount += 1;
      }
    } else {
      byBranch.set(branchName, {
        runs: 1,
        finishedRuns: isFinished ? 1 : 0,
        successRuns: isSuccess ? 1 : 0,
        totalDurationMs: dur > 0 ? dur : 0,
        durationCount: dur > 0 ? 1 : 0,
        group,
      });
    }
  }

  return [...byBranch.entries()]
    .map(([branch, acc]) => ({
      branch,
      group: acc.group,
      runs: acc.runs,
      successRate: acc.finishedRuns === 0 ? 0 : acc.successRuns / acc.finishedRuns,
      avgDurationMs: acc.durationCount > 0 ? acc.totalDurationMs / acc.durationCount : 0,
    }) satisfies BranchStats)
    .sort((a, b) => {
      const diff = b.runs - a.runs;
      return diff !== 0 ? diff : a.branch.localeCompare(b.branch);
    })
    .slice(0, limit);
}
